import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  DoneAll as DoneAllIcon,
  Check as CheckIcon
} from '@material-ui/icons';
import { diffChars } from 'diff';
import { red, lightGreen } from '@material-ui/core/colors';

import auth from '../../libs/auth';

export interface Output {
  passed: boolean
  inputs: string[]
  expectedOutput: string
  actualOutput: string
}

export interface Props {
  activityIndex?: number
  outputs: Output[]
  showTestCase?: boolean
  onMarkAsCorrect?(activityIndex: number, outputIndex: number): void
  onMarkAllAsCorrect?(activityIndex: number): void
}

const useStyle = makeStyles(() => ({
  outputTable: {
    width: '100%',

    '& tr th, & tr td': {
      verticalAlign: 'text-top',
      width: 'auto',
      textAlign: 'left',
      padding: '10px 0',

      '&:nth-child(1)': {
        whiteSpace: 'nowrap',
        paddingRight: '20px'
      },

      '&:nth-child(2)': {
        width: '100%'
      }
    }
  },
  outputTextBox: {
    margin: 0
  }
}));

const findDifference = (expected: string, actual: string) => {
  const characterDiffs = diffChars(expected, actual);
  return characterDiffs.map((diffOutput, index) => {
    const key = `${diffOutput.value}_${index}`;
    if (diffOutput.added) {
      return (
        <span style={{ backgroundColor: lightGreen[200] }} key={key}>
          {diffOutput.value.replace(/^\n$/, ' \n')}
        </span>
      );
    }
    if (diffOutput.removed) {
      return (
        <span style={{ backgroundColor: red[200] }} key={key}>{diffOutput.value.replace(/^\n$/, ' \n')}</span>
      );
    }
    return <span key={key}>{diffOutput.value}</span>;
  });
};

const TestOutputs = ({
  outputs,
  activityIndex,
  showTestCase,
  onMarkAsCorrect,
  onMarkAllAsCorrect,
}: Props) => {
  const [showOutputDifference, setShowOutputDifference] = useState(false);
  const [hasFailedTest, setHasFailedTest] = useState(false);

  const classes = useStyle();

  useEffect(() => {
    const failed = outputs.some((output) => !output.passed);
    setHasFailedTest(failed);
  }, [outputs]);

  return (
    <Box p={0.3}>
      {hasFailedTest && (
        <Box pl={2} pb={1}>
          <Grid container justify="space-between" alignItems="center">
            <Grid item>
              <Tooltip title="Show a colorized difference between the expected output and actual output to easily spot incorrect values, especially stray whitespaces.">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showOutputDifference}
                      onChange={() => setShowOutputDifference(!showOutputDifference)}
                      inputProps={{ 'aria-label': 'primary checkbox' }}
                    />
                  }
                  label={<Typography>Show Difference</Typography>}
                />
              </Tooltip>
            </Grid>
            <Grid item>
              {(auth.isAdmin()) &&
              !!onMarkAllAsCorrect && (
                <Tooltip title="Mark all as correct">
                  <Button
                    type="button"
                    size="small"
                    startIcon={<DoneAllIcon />}
                    color="primary"
                    onClick={() => {
                      if (activityIndex !== undefined)
                        onMarkAllAsCorrect(activityIndex);
                    }}
                  >
                    Mark all as correct
                  </Button>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {outputs.map((out, index) => {
        const hidden = !showTestCase && /^hidden$/i.test(out.actualOutput) && /^hidden$/i.test(out.expectedOutput);
        return (
          <Accordion
            key={index}
            defaultExpanded={false}
          >
            <AccordionSummary expandIcon={!hidden && <ExpandMoreIcon />} disabled={hidden}>
              <Grid container justify="space-between" alignItems="center">
                <Grid item>
                  <Grid container alignItems="center" spacing={1} style={{ color: out.passed ? 'green' : 'red' }}>
                    <Grid item style={{lineHeight: 1}}>
                      {out.passed ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                    </Grid>
                    <Grid item>
                      <Typography>{out.passed ? 'Test Passed' : 'Test Failed'}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography color="textPrimary">({`${index + 1} of ${outputs.length}`})</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  {(auth.isAdmin()) &&
                  !out.passed &&
                  !!onMarkAsCorrect && (
                    <Tooltip title="Mark as correct">
                      <IconButton
                        type="button"
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (activityIndex !== undefined)
                            onMarkAsCorrect(activityIndex, index);
                        }}
                      >
                        <CheckIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {hidden && <Typography><small>hidden</small></Typography>}
                </Grid>
              </Grid>
            </AccordionSummary>

            <AccordionDetails>
              <table className={classes.outputTable}>
                <tbody>
                  <tr>
                    <td>Inputs</td>
                    <td>{(out.inputs || []).join(', ')}</td>
                  </tr>
                  <tr>
                    <td>Expected Answer</td>
                    <td>
                      <pre className={classes.outputTextBox}>{out.expectedOutput}</pre>
                    </td>
                  </tr>
                  <tr>
                    <th style={{ color: out.passed ? 'green' : 'red' }}>Your Answer</th>
                    <th>
                      <pre className={classes.outputTextBox} data-tip={out.actualOutput}>{showOutputDifference ? findDifference(out.expectedOutput, out.actualOutput) : out.actualOutput}</pre>
                    </th>
                  </tr>
                </tbody>
              </table>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TestOutputs;
